import { useAssets } from "@hooks/useAssets";
import useHeaderChange from "@hooks/useHeaderChange";
import { useParams } from "react-router";
import AssetsSkeleton from "./AssetsSkeleton";
import DataGrid, { Column } from "devextreme-react/data-grid";
import { cellRenderer, headerRenderer } from "../gifting/grid-utils";
import { formatNumber } from "devextreme/localization";
import NoDataMessage from "@/components/NoDataMessage";
import type { AssetGroup, FinancialAccount } from "@/types/types";
import withDisclaimer from "@/hoc/withDisclaimer";
import { cn } from "@/helper/helper";

function Assets() {
  const { id } = useParams();
  useHeaderChange(id!, "Assets");
  const { data, isLoading, isError, isRefetching } = useAssets(id!);

  if (isLoading || isRefetching) return <AssetsSkeleton />;
  if (isError) return <div>Failed fetching Assets</div>;
  if (!data?.data?.assetGroups?.length) return <NoDataMessage />;

  const assetGroups: AssetGroup[] = data?.data?.assetGroups;
  const fas = assetGroups?.flatMap((x) => x.financialAccounts);
  const contacts = data?.data?.contacts || [];

  if (!fas.length) return <NoDataMessage />;

  return (
    <DataGrid
      className="ao-grid grid-sticky-headers"
      dataSource={fas}
      showBorders={true}
      showRowLines={true}
      showColumnLines={true}
      remoteOperations={false}
      wordWrapEnabled={true}
      keyExpr={"externalCode"}
      onCellPrepared={(e) => {
        if (e.rowType === "header" || e.column.dataField === "asset-name") {
          e.cellElement.style.backgroundColor = "var(--primary-color)";
          e.cellElement.style.color = "white";
        }
      }}
    >
      <Column
        dataField="asset-name"
        allowSorting={false}
        allowEditing={false}
        minWidth={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`asset-name`, "Asset")}
        cellRender={(cellData) => {
          const fa: FinancialAccount = cellData.data;
          return cellRenderer(
            cellData,
            `asset-name-${fa.externalCode}`,
            fa?.assetsName,
            "",
            "flex items-center justify-center text-sm !py-[0.7344rem] font-medium h-full"
          );
        }}
      />
      <Column
        dataField="type"
        allowSorting={false}
        allowEditing={false}
        minWidth={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`type`, "Type")}
        cellRender={(cellData) => {
          const fa: FinancialAccount = cellData.data;
          return cellRenderer(
            cellData,
            `type-${fa.externalCode}`,
            fa?.assetsType,
            "",
            "flex h-full items-center justify-center text-sm !py-[0.7344rem] min-h-[49px]"
          );
        }}
      />
      <Column
        dataField="ownership-name"
        allowSorting={false}
        allowEditing={false}
        minWidth={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`ownership-name`, "ownership")}
        cellRender={(cellData) => {
          const fa = cellData.data as FinancialAccount;
          const assGroup = assetGroups.find((x) => x.externalCode === fa.assetGroupId);

          return cellRenderer(
            cellData,
            "ownership-name",
            assGroup?.assetsName,
            "",
            "flex h-full items-center justify-center text-sm !py-[0.7344rem] min-h-[49px]"
          );
        }}
      />

      <Column
        dataField="primary-bene"
        allowSorting={false}
        allowEditing={false}
        minWidth={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`primary-bene`, "Primary")}
        cellRender={(cellData) => {
          const fa: FinancialAccount = cellData.data;

          return (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {fa?.beneficiaries
                ?.filter((x) => x.type === "Primary" || x.type === "Primary TOD")
                ?.map(({ assetsName, externalCode, type, validationNeeded, contactId }) => {
                  const foundContact = contacts.find(
                    (x) => x.externalCode === contactId || assetsName === x.externalCode
                  );

                  return (
                    <div
                      id={`primary-${externalCode}`}
                      key={`primary-${externalCode}`}
                      className={cn(
                        "relative flex h-full items-center justify-center py-[0.7344rem] print:px-1 print:py-1 print:!text-[10px]",
                        validationNeeded && "!border !border-danger",
                        foundContact?.isDeceased && "!bg-neutral-400"
                      )}
                    >
                      {type === "Primary TOD" ? (
                        <span>
                          {assetsName}
                          <span className="font-semibold"> (TOD)</span>
                        </span>
                      ) : (
                        assetsName
                      )}
                    </div>
                  );
                })}
            </div>
          );
        }}
      />
      <Column
        dataField="primary-bene-percentage"
        allowSorting={false}
        allowEditing={false}
        width={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`primary-bene-percentage`, "%")}
        cellRender={(cellData) => {
          const fa: FinancialAccount = cellData.data;

          return (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {fa?.beneficiaries
                ?.filter((x) => x.type === "Primary" || x.type === "Primary TOD")
                ?.map(({ percent, externalCode, validationNeeded, contactId, assetsName }) => {
                  const foundContact = contacts.find(
                    (x) => x.externalCode === contactId || assetsName === x.externalCode
                  );

                  return cellRenderer(
                    cellData,
                    `primary-percentage-${externalCode}`,
                    formatNumber(percent / 100, "#.##%"),
                    "",
                    cn(
                      "flex items-center justify-center text-sm h-full !py-[0.7344rem] min-h-[49px]",
                      validationNeeded && "!border !border-danger",
                      foundContact?.isDeceased && "!bg-neutral-400"
                    )
                  );
                })}
            </div>
          );
        }}
      />
      <Column
        dataField="contingent"
        allowSorting={false}
        allowEditing={false}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`contingent`, "contingent")}
        cellRender={(cellData) => {
          const fa: FinancialAccount = cellData.data;

          return (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {fa?.beneficiaries
                ?.filter((x) => x.type === "Contingent" || x.type === "Contingent TOD")
                .map(({ externalCode, assetsName, type, validationNeeded, contactId }) => {
                  const foundContact = contacts.find(
                    (x) => x.externalCode === contactId || assetsName === x.externalCode
                  );

                  return (
                    <div
                      id={`contingent-${externalCode}`}
                      key={`contingent-${externalCode}`}
                      className={cn(
                        "relative flex h-full items-center justify-center py-[0.7344rem] print:px-1 print:py-1 print:!text-[10px]",
                        validationNeeded && "!border !border-danger",
                        foundContact?.isDeceased && "!bg-neutral-400"
                      )}
                    >
                      {type === "Contingent TOD" ? (
                        <span>
                          {assetsName}
                          <span className="font-semibold"> (TOD)</span>
                        </span>
                      ) : (
                        assetsName
                      )}
                    </div>
                  );
                })}
            </div>
          );
        }}
      />
      <Column
        dataField="contingent-percentage"
        allowSorting={false}
        allowEditing={false}
        width={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`contingent-percentage`, "%")}
        cellRender={(cellData) => {
          const fa: FinancialAccount = cellData.data;

          return (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {fa?.beneficiaries
                ?.filter((x) => x.type === "Contingent" || x.type === "Contingent TOD")
                ?.map(({ percent, externalCode, validationNeeded, contactId, assetsName }) => {
                  const foundContact = contacts.find(
                    (x) => x.externalCode === contactId || assetsName === x.externalCode
                  );

                  return cellRenderer(
                    cellData,
                    `contingent-percentage-${externalCode}`,
                    formatNumber(percent / 100, "#.##%"),
                    "",
                    cn(
                      "flex items-center justify-center text-sm h-full !py-[0.7344rem] min-h-[49px]",
                      validationNeeded && "!border !border-danger",
                      foundContact?.isDeceased && "!bg-neutral-400"
                    )
                  );
                })}
            </div>
          );
        }}
      />
    </DataGrid>
  );
}

const AssetsWithDisclaimer = withDisclaimer(Assets);

export default AssetsWithDisclaimer;
